import { fromJS, List } from 'immutable';
import moment from 'moment';
import { Message } from 'flow/types/Chats';
import { TypedMap } from 'flow/types/TypedMap';
import { VideoChatMessage } from 'flow/types/VideoChat';

/**
 * Decides which messages the list shows and in what order. Kept apart from the
 * row/grouping build so that the in-meeting guest-message case - which applies
 * to exactly one session, only while a meeting is running - does not thread
 * conditionals through the layout code.
 *
 * Pure and List-in/List-out, so ordering can be tested without rendering.
 */

/**
 * Numeric id from a message key, for ordering. Keys come in two forms:
 * 'chatmessage-<n>' from the chat service and 'failedchatmessage-<n>' for a
 * send that failed locally. A failed message is always newer than anything the
 * server has returned, so it sorts to the end rather than by its own id, which
 * is a separate sequence and not comparable with chat-service ids.
 */
export const chatMessageId = (key: string): number => {
    const match = /^chatmessage-(\d+)$/.exec(key ?? '');
    return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
};

/**
 * Build a chat-service-shaped Message from a video-chat relay message so it can
 * be merged into the timeline. Keyed 'ext-{id}' to stay distinct from
 * chat-service keys, and typed 'external' so the row builder renders it as a
 * guest message.
 */
export const synthesizeExternalMessage = (
    m: TypedMap<VideoChatMessage>,
): TypedMap<Message> => {
    // A frame with a missing/invalid ts would make new Date(undefined)
    // throw on toISOString() and blank the whole panel. Fall back to now so
    // the message still renders, in roughly the right place.
    const ts = m.get('ts');
    const sent = new Date(
        typeof ts === 'number' ? ts : Date.now(),
    ).toISOString();
    return fromJS({
        type: 'external',
        key: `ext-${m.get('id')}`,
        sent,
        message: m.get('text'),
        from: { name: m.get('name') },
        read: true,
        failed: false,
        alsoAsSMS: false,
        DTOSubtype: '',
        stylings: [],
        reactions: [],
    }) as unknown as TypedMap<Message>;
};

/**
 * Merge the chat-service messages, the locally-failed ones and any in-meeting
 * guest messages into a single ordered list.
 *
 * Ordering deliberately differs by case. Without guests, order by message id:
 * that is the server's own ordering and does not depend on clocks agreeing.
 * With guests there is no shared id sequence, so order by `sent` - and by
 * valueOf() rather than string compare, because chat-service `sent` is
 * offset-form while relay `sent` is UTC 'Z' and the two lex-sort wrongly.
 */
/** Orders on a [failed, key] pair: failed sends after everything else, and
 *  within each group on the branch's own key. */
const compareOrdering = (a: number[], b: number[]): number =>
    a[0] - b[0] || a[1] - b[1];

/** A send that failed locally, rather than anything the server has returned. */
export const messageFailedToSend = (message: TypedMap<Message>): boolean =>
    (message.get('key') ?? '').startsWith('failedchatmessage-');

export const mergeDisplayMessages = (
    latestMessages: List<TypedMap<Message>> | undefined,
    failedMessages: List<TypedMap<Message>>,
    externalMessages?: List<TypedMap<VideoChatMessage>>,
): List<TypedMap<Message>> => {
    const chatServiceMessages = (
        latestMessages ?? List<TypedMap<Message>>()
    ).concat(failedMessages);

    // A failed send is a state to act on, not a point in history, so it sits at
    // the end whichever key is in play. By id it landed there anyway, through
    // chatMessageId's fallback; by time it would sort back to the moment the
    // send was attempted and read as delivered. Sorting on it explicitly keeps
    // the two branches agreeing, so a message does not move when a guest joins.
    const failedLast = (m: TypedMap<Message>) =>
        messageFailedToSend(m) ? 1 : 0;

    // sortBy returns a lazy Iterable in Immutable 3, so materialize it: the
    // result is iterated more than once by the row builder.
    if (!externalMessages || externalMessages.isEmpty()) {
        return chatServiceMessages
            .sortBy(
                (m) => [failedLast(m), chatMessageId(m.get('key'))],
                compareOrdering,
            )
            .toList();
    }

    return chatServiceMessages
        .concat(externalMessages.map(synthesizeExternalMessage))
        .sortBy(
            (m) => [failedLast(m), moment(m.get('sent')).valueOf()],
            compareOrdering,
        )
        .toList();
};
